// Minor-unit arithmetic for the finance UI. The table below is the one in
// internal/money/money.go — same 16 currencies, same exponents — so a
// currency the server cannot hold is not spendable here either.
//
// The grammar is NOT the server's: internal/money.ParseMajor accepts "+5",
// "5." and ".5" and rejects "1,56" and "1 234.56", while toMinor does the
// opposite (both verified by running them — task-22 report). Nothing parses
// the same string on both sides today: this module reads what a human types
// into a form and sends integers, ParseMajor reads the import spreadsheet.
//
// Amounts travel as major-unit strings inside forms and become integers
// exactly once, on submit (toMinor). Nothing here ever returns a fractional
// number: toMinor returns an integer or null, fromMinor returns a string.
const EXPONENTS: Record<string, number> = {
  EUR: 2,
  USD: 2,
  RSD: 2,
  GBP: 2,
  CHF: 2,
  HUF: 2,
  CZK: 2,
  PLN: 2,
  BAM: 2,
  NOK: 2,
  SEK: 2,
  DKK: 2,
  CAD: 2,
  AUD: 2,
  JPY: 0,
  KRW: 0,
};

/**
 * Minor-unit digits of `currency` (upper-case ISO 4217, as the API sends it).
 * Throws for a currency not in the table: falling back to two decimals would
 * quietly turn ¥16000 into ¥160.
 */
export function exponent(currency: string): number {
  const e = EXPONENTS[currency];
  if (e === undefined) throw new Error(`unknown currency ${currency}`);
  return e;
}

/**
 * "1234.56" | "1234,56" | "1 234,56" → 123456. A decimal comma and spaces in
 * the thousands are understood; anything else that is not a plain decimal
 * number is refused with null, and so is a string carrying more fractional
 * digits than the currency has — never a rounded value. An unknown currency
 * throws instead: that is a bug in the caller, not bad input from a human.
 */
export function toMinor(major: string, currency: string): number | null {
  const exp = exponent(currency);
  const s = major.trim().replace(',', '.').replace(/\s+/g, '');
  if (!/^-?\d+(\.\d+)?$/.test(s)) return null;
  const neg = s.startsWith('-');
  const [whole = '0', frac = ''] = (neg ? s.slice(1) : s).split('.');
  if (frac.length > exp) return null;
  const digits = whole + frac.padEnd(exp, '0');
  const v = Number.parseInt(digits, 10);
  // Past 2^53 parseInt no longer returns the digits it was given, so the
  // amount would be wrong by an unbounded margin.
  if (!Number.isSafeInteger(v)) return null;
  if (v === 0) return 0; // "-0.00" must not come back as -0
  return neg ? -v : v;
}

/**
 * 123456, "EUR" → "1234.56": plain digits, no thousands separators, no
 * currency symbol — the shape an <input> wants. For a display string with
 * the currency symbol use formatMoney (lib/format.ts); note that one divides
 * by 100 unconditionally, so it is wrong for zero-decimal currencies.
 * Throws when `minor` is not an exact integer — 123.456 would otherwise
 * render as "123.4.56" and NaN as "N.aN".
 */
export function fromMinor(minor: number, currency: string): string {
  const exp = exponent(currency);
  if (!Number.isSafeInteger(minor)) {
    throw new Error(`money: ${minor} is not an exact minor-unit amount`);
  }
  const neg = minor < 0;
  const abs = Math.abs(minor).toString().padStart(exp + 1, '0');
  const body = exp === 0 ? abs : `${abs.slice(0, -exp)}.${abs.slice(-exp)}`;
  return neg ? `-${body}` : body;
}

/** The `step` of an <input type="number"> for this currency: "0.01" | "1". */
export function stepFor(currency: string): string {
  const exp = exponent(currency);
  return exp === 0 ? '1' : `0.${'0'.repeat(exp - 1)}1`;
}

/**
 * Every currency the arithmetic above knows, sorted — the list the finance
 * account dialog offers. The server accepts exactly the currencies
 * internal/money knows (422 otherwise), and this table is that one.
 */
export const CURRENCIES: readonly string[] = Object.keys(EXPONENTS).sort();

/**
 * 123456, "EUR" → "€1,234.56"; 16000, "JPY" → "¥16,000". The display string
 * for finance screens. formatMoney (lib/format.ts) cannot be used here: it
 * divides by 100 whatever the currency is, so it renders ¥16,000 as ¥160.00.
 *
 * Intl is fed fromMinor's exact decimal STRING, not `minor / 10 ** exp`: the
 * division is a double and at the top of the safe-integer range it lands on
 * the wrong cent (…409.90 instead of …409.91). Throws on an unknown currency
 * and on a non-integer amount — both via the helpers above, same policy.
 */
export function formatMinor(minor: number, currency: string): string {
  const exp = exponent(currency);
  const exact = fromMinor(minor, currency);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: exp,
      maximumFractionDigits: exp,
      // A decimal string is a legal argument since Intl.NumberFormat V3
      // (ES2023); tsconfig's lib is ES2022, whose signature only admits
      // number | bigint, hence the cast. Passing the string is the point of
      // the function — see the note above.
    }).format(exact as unknown as number);
  } catch {
    // An ICU build without this currency: still show the exact figure rather
    // than nothing, with the code spelled out.
    return `${exact} ${currency}`;
  }
}
